import {FormattedMessage, useIntl} from 'react-intl';
import React, {ReactNode, useEffect, useMemo, useRef} from 'react';
import {
	Accordion,
	EmptyAccordion,
	FlexColumn,
	FlexRow,
	Heading,
	Modal,
	SubHeading,
	Text,
	theme,
	IconTooltip,
} from '@forecast-it/design-system';
import {
	getCombinedTaskMode,
	getLocalDateFromCanvasTimelineDate,
	HEATMAP_CHECKMARK_LENIENCY_PERCENTAGE,
	VISUALIZATION_MODE,
} from '../canvas-timeline/canvas_timeline_util';
import {getMinutesAllocatedVariation} from '../heatmap/MinutesAllocatedVariationsUtils';
import DataManager from '../DataManager';
import Util from '../../../forecast-app/shared/util/util';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {INITIALS_SIZE} from '../../../constants';
import {NO_ACCESS_DISTRIBUTION_TYPE} from '../constants';
import PersonInitials from '../../../forecast-app/shared/components/person/person_initials';
import styled from 'styled-components';
import {getProjectGroupLink, getProjectLinkAppendLastViewed} from '../../../forecast-app/shared/util/LinkUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {ContentWarning} from './UtilizationModalWarnings';
import {Distribution, DistributionEntry, SchedulingOptions, UtilizationModal_company} from './UtilizationModal_viewer';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {UtilizationModalV2Table} from './UtilizationModalV2Table';
import {profilePicSrc} from '../../../directApi';

const UtilizationSubHeader = styled.div`
	font: ${theme.fonts.heading[3]};
	font-weight: 400;
`;

type GridRowProps = {
	narrow?: boolean;
	columns: number;
	children: ReactNode | undefined;
};

const gridColumns = ({columns}) => {
	if (columns === 3) {
		return 'auto 21% 15%';
	} else if (columns === 4) {
		return 'auto 21% 25% 20%';
	} else {
		return 'auto 18% 15% 26% 9%';
	}
};

const GridRow = styled.div<GridRowProps>`
	display: grid;
	grid-template-columns: ${gridColumns};
	column-gap: ${theme.spacing[400]};
	align-items: center;
	width: 100%;
	border-bottom: solid 1px ${theme.colors.border.separator};
	padding: ${({narrow}) => theme.spacing[narrow ? 400 : 600]} ${theme.spacing[200]}
		${({narrow}) => theme.spacing[narrow ? 400 : 600]} ${theme.spacing[200]};

	&:first-child {
		padding-top: ${theme.spacing[200]};
	}

	&:last-child {
		padding-bottom: ${({narrow}) => theme.spacing[narrow ? 0 : 200]};
		border-bottom: none;
	}
`;

export interface UtilizationModalV2Props {
	closeModal: () => void;
	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	pageComponent: any;
	person: {
		person: {
			id: string;
		};
		name: string;
		initials?: string;
		profilePictureId?: string;
	};
	company: UtilizationModal_company;
	startDate: number;
	endDate: number;
	stepLabel: string;
	actualPersonId: string;
	distributionMap: Map<string, Distribution>;
	schedulingOptions?: SchedulingOptions;
	workingMinutes: number;
	minutesAvailable: number;
	minutesAllocatedVariations: {[key: string]: number};
}

function Avatar({person}: Pick<UtilizationModalV2Props, 'person'>) {
	return (
		<>
			{person.profilePictureId ? (
				<img crossOrigin="use-credentials" height="32" src={profilePicSrc(person.profilePictureId)} alt="" />
			) : (
				<PersonInitials
					size={INITIALS_SIZE.LARGE}
					initials={person.initials ? person.initials : Util.getInitials(`${person.name}`)}
				/>
			)}
		</>
	);
}

const CellRightAligned = ({children}) => (
	<FlexRow alignItems={'center'} justifyContent={'end'} gap={'s'}>
		{children}
	</FlexRow>
);

function isAboveThreshold(capacity: number, demand: number) {
	const isOverallocated = demand > capacity;
	return isOverallocated && (!capacity || capacity / demand - 1 < HEATMAP_CHECKMARK_LENIENCY_PERCENTAGE / 100);
}

function UtilizationModalV2({
	pageComponent,
	person,
	company,
	startDate: canvasTimelineStartDate,
	endDate: canvasTimelineEndDate,
	stepLabel,
	distributionMap,
	actualPersonId,
	closeModal,
	workingMinutes,
	minutesAvailable,
	schedulingOptions,
	minutesAllocatedVariations,
}: UtilizationModalV2Props): JSX.Element {
	const scrollRef = useRef(null);
	const hideSoft = schedulingOptions ? schedulingOptions.hideSoft : false;
	const hideHard = schedulingOptions ? schedulingOptions.hideHard : false;
	const calcWin = schedulingOptions ? schedulingOptions.calcWin : false;
	const minutesAllocated = getMinutesAllocatedVariation(minutesAllocatedVariations, hideSoft, hideHard, calcWin);

	useEffect(() => {
		trackEvent('Utilization Modal', 'Opened');
	}, []);

	const intl = useIntl();
	const {formatMessage, formatDate} = intl;
	const isInActualMode = getCombinedTaskMode(company) === VISUALIZATION_MODE.TASK_ACTUAL;

	const dateFormatted = useMemo(() => {
		const startDate = getLocalDateFromCanvasTimelineDate(canvasTimelineStartDate);
		const endDate = getLocalDateFromCanvasTimelineDate(canvasTimelineEndDate);
		const isSameDay = canvasTimelineStartDate === canvasTimelineEndDate;
		let dateFormatted = formatDate(startDate);
		if (!isSameDay) {
			dateFormatted += ' - ' + formatDate(endDate);
		}
		return dateFormatted;
	}, [canvasTimelineStartDate, canvasTimelineEndDate]);

	const taskEstimatesLabel = formatMessage({id: 'scheduling.utilization.task_estimates'});
	const allocationsLabel = formatMessage({id: 'scheduling.utilization.allocations'});

	const hasAccessToProject = project => {
		return (
			hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL) ||
			(project &&
				DataManager.getProjectPersonsByProjectId(pageComponent, project.id).find(
					projectPerson => projectPerson.personId === actualPersonId
				))
		);
	};

	const remaining = minutesAvailable - minutesAllocated;
	const showWarning = isAboveThreshold(minutesAvailable, minutesAllocated);
	const {timeOff, entries, hasRemaining, hasActuals} = useMemo(() => {
		let timeOff = 0;
		let hasRemaining = false;
		let hasActuals = false;
		const entries: DistributionEntry[] = [];
		for (const [id, value] of distributionMap.entries()) {
			const idType = id ? Util.getTypeFromGlobalID(id) : '';
			let name;
			let link;
			let companyProjectId;
			let customProjectId;
			let projectColor;
			let status;

			let allocationMinutes = 0;
			const allocationMinutesHard = value.allocationMinutes - value.minutesSoft;
			let plannedAllocationMinutes = 0;
			if (!hideSoft && hideHard) {
				allocationMinutes = calcWin ? value.minutesSoftWin : value.minutesSoft;
				plannedAllocationMinutes = calcWin ? value.plannedAllocationMinutesSoftWin : value.plannedAllocationMinutesSoft;
			} else if (hideSoft && !hideHard) {
				allocationMinutes = allocationMinutesHard;
				plannedAllocationMinutes = value.plannedAllocationMinutesHard;
			} else if (!hideSoft && !hideHard) {
				allocationMinutes = allocationMinutesHard + (calcWin ? value.minutesSoftWin : value.minutesSoft);
				plannedAllocationMinutes = calcWin ? value.plannedAllocationMinutesWin : value.plannedAllocationMinutes;
			}

			const actual = value.timeRegMinutes;
			const taskMinutes = isInActualMode ? value.actualTaskMinutes : value.plannedTaskMinutes;
			const useTaskMinutesForRemaining = taskMinutes > 0 && taskMinutes > allocationMinutes;
			const remaining = useTaskMinutesForRemaining ? taskMinutes : allocationMinutes;
			const total = remaining + actual;

			let source = useTaskMinutesForRemaining ? taskEstimatesLabel : allocationsLabel;

			if (remaining > 0) {
				hasRemaining = true;
			}

			if (actual) {
				hasActuals = true;
			}

			let planned;
			let over;
			if (remaining <= 0) {
				const useTaskMinutesForPlanned = value.plannedTaskMinutes > plannedAllocationMinutes;
				planned = useTaskMinutesForPlanned ? value.plannedTaskMinutes : plannedAllocationMinutes;
				source = useTaskMinutesForPlanned ? taskEstimatesLabel : allocationsLabel;
				over = actual - planned;
			}

			if (!remaining && !actual && !planned) {
				continue;
			}

			const isHardAllocation = value.plannedAllocationMinutesHard || allocationMinutesHard;
			const isSoftAllocation =
				value.plannedAllocationMinutesSoft ||
				value.plannedAllocationMinutesSoftWin ||
				value.minutesSoft ||
				value.minutesSoftWin;

			if (!useTaskMinutesForRemaining) {
				if (isHardAllocation && isSoftAllocation) {
					source += ' (Hard & Soft)';
				} else if (isHardAllocation) {
					source += ' (Hard)';
				} else if (isSoftAllocation) {
					source += ' (Soft)';
				}
			}

			const warning = isAboveThreshold(planned, actual);

			if (idType === 'ProjectGroupType') {
				const projectGroup = DataManager.getProjectGroupById(pageComponent, id);
				const firstProject = DataManager.getProjectsByProjectGroupId(pageComponent, projectGroup.id)[0];
				name = projectGroup.name;
				companyProjectId = projectGroup.companyProjectGroupId;
				projectColor = projectGroup.color;
				link = hasAccessToProject(firstProject) ? getProjectGroupLink(projectGroup.companyProjectGroupId) : undefined;
			} else if (idType === 'ProjectType') {
				const project = DataManager.getProjectById(pageComponent, id);
				name = project.name;
				companyProjectId = project.companyProjectId;
				customProjectId = project.customProjectId;
				projectColor = project.projectColor;
				status = project.status;
				link = hasAccessToProject(project)
					? getProjectLinkAppendLastViewed(project.companyProjectId, project.customProjectId)
					: undefined;
			} else if (idType === 'IdleTime') {
				const idleTime = DataManager.getIdleTimeById(pageComponent, id);
				name = idleTime.name;
				source = allocationsLabel;
				const isTimeOff = !idleTime.isInternalTime;
				if (isTimeOff) {
					timeOff += value.minutes;
					continue;
				}
			} else if (idType === NO_ACCESS_DISTRIBUTION_TYPE) {
				name = formatMessage({id: 'scheduling.no_access'});
			} else {
				// Unknown entity
				continue;
			}
			entries.push({
				id,
				name,
				link,
				planned,
				actual,
				remaining,
				source,
				total,
				over,
				warning,
				companyProjectId,
				customProjectId,
				projectColor,
				status,
			});
		}
		return {
			timeOff,
			entries,
			hasRemaining,
			hasActuals,
		};
	}, [distributionMap, workingMinutes, minutesAllocated, minutesAvailable]);

	const actualsAndRemainingPeriod = hasActuals && hasRemaining;
	const actualsPeriod = !hasRemaining;
	const remainingPeriod = !hasActuals && hasRemaining;

	if (entries.length === 0) {
		entries.push({
			companyProjectId: undefined,
			customProjectId: undefined,
			projectColor: undefined,
			status: undefined,
			name: 'N/A',
			source: 'N/A',
			planned: 0,
			actual: 0,
			over: 0,
		});
	}

	const trackExpand = value => {
		trackEvent('Utilization Modal - ' + value, 'Expanded');
	};
	const trackCollapse = value => {
		trackEvent('Utilization Modal - ' + value, 'Collapsed');
	};

	const getCorrectModalSize = () => {
		return actualsAndRemainingPeriod || actualsPeriod ? Modal.SIZE.EXTRA_EXTRA_LARGE : Modal.SIZE.LARGE;
	};

	const demandLabel = formatMessage({id: 'scheduling.utilization.demand'});
	const timeEntriesLabel = formatMessage({id: 'scheduling.utilization.time_entries'});
	const entriesLabel = actualsPeriod ? timeEntriesLabel : demandLabel;
	const entriesValue = actualsPeriod ? 'Time Entries' : 'Demand';

	return (
		<Modal size={getCorrectModalSize()} onCloseModal={closeModal}>
			<Modal.Title>
				<FlexRow gap={'m'}>
					<Avatar person={person} />
					<Heading size={3}>{person.name}</Heading>
					<UtilizationSubHeader>
						<FormattedMessage id={'scheduling.utilization'} />
					</UtilizationSubHeader>
				</FlexRow>
			</Modal.Title>
			<Modal.Content ref={scrollRef}>
				<FlexRow gap={'xl'}>
					<Text color={'medium'}>
						<FormattedMessage id={'scheduling.utilization_date_range'} />
					</Text>
					<SubHeading>
						{stepLabel} ({dateFormatted})
					</SubHeading>
				</FlexRow>
				<FlexColumn gap={'xs'}>
					<Accordion
						selectionType={Accordion.SelectionType.SINGLE}
						collapsible={true}
						background
						border
						disabled={false}
						defaultValue={entriesValue}
						onOpen={trackExpand}
						onClose={trackCollapse}
					>
						<Accordion.Item
							alignment={Accordion.Alignment.LEFT}
							content={Util.convertMinutesToFullHour(minutesAvailable, intl, true)}
							contentAlignment={Accordion.Alignment.RIGHT}
							label={formatMessage({id: 'common.capacity'})}
							value={'Capacity'}
						>
							<>
								<GridRow columns={3}>
									<SubHeading>
										<FormattedMessage id={'common.working_hours'} />
									</SubHeading>
									<CellRightAligned>
										<SubHeading>
											<FormattedMessage id={'common.time_off'} />
										</SubHeading>
									</CellRightAligned>
									<CellRightAligned>
										<SubHeading>
											<FormattedMessage id={'common.total'} />
										</SubHeading>
									</CellRightAligned>
								</GridRow>
								<GridRow columns={3}>
									<Text>{Util.convertMinutesToFullHour(workingMinutes, intl, true)}</Text>
									<CellRightAligned>
										<Text>{Util.convertMinutesToFullHour(timeOff, intl, true)}</Text>
									</CellRightAligned>
									<CellRightAligned>
										<Text>{Util.convertMinutesToFullHour(minutesAvailable, intl, true)}</Text>
									</CellRightAligned>
								</GridRow>
							</>
						</Accordion.Item>
						<Accordion.Item
							alignment={Accordion.Alignment.LEFT}
							content={
								<FlexRow gap={'xs'}>
									<ContentWarning showWarning={showWarning} hasRemaining={hasRemaining}>
										{Util.convertMinutesToFullHour(minutesAllocated, intl, true)}
									</ContentWarning>
								</FlexRow>
							}
							contentAlignment={Accordion.Alignment.RIGHT}
							label={entriesLabel}
							value={entriesValue}
						>
							<UtilizationModalV2Table
								actualsAndRemainingPeriod={actualsAndRemainingPeriod}
								remainingPeriod={remainingPeriod}
								actualsPeriod={actualsPeriod}
								entries={entries}
								closeModal={closeModal}
								scrollRef={scrollRef}
							/>
						</Accordion.Item>
					</Accordion>
					<EmptyAccordion
						label={formatMessage({id: 'scheduling.utilization.remaining_capacity'})}
						contentAlignment={Accordion.Alignment.RIGHT}
						tooltip={
							<IconTooltip.Item
								title={formatMessage({id: 'scheduling.utilization.remaining_capacity'})}
								details={formatMessage({id: 'utilization.table.remaining_capacity.tooltip'})}
							/>
						}
						content={
							<FlexRow gap={'xs'} justifyContent={'end'}>
								<ContentWarning showWarning={showWarning} hasRemaining={hasRemaining}>
									{Util.convertMinutesToFullHour(remaining, intl, true)}
								</ContentWarning>
							</FlexRow>
						}
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.close'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}

export default UtilizationModalV2;
